import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  addRedemptionChannelService,
  editRedemptionChannelService,
  getRedemptionChannelByIdService,
  getRedemptionChannelsService,
} from "../../services/crosspay-service";
import { AsyncAppThunk } from "../application-state";
import { addSnack, showLoaderWhile } from "./ui";

import { EqualFilter, Filter } from "libs/models/filter";
import { PaginatedList } from "libs/models/paginated-list";
import { getFilterQuery } from "utils/filter";

import { FilterTab } from "components/tab-filter/tab-filter.props";
import {
  RedemptionChannelModel,
  RedemptionChannelRequestModel,
} from "libs/models/branches";
import { ValidateAddRedemptionChannelState } from "pages/AddRedemptionChannel/add-redemption-channel.policy";
import { ValidateEditRedemptionChannelState } from "pages/EditRedemptionChannel/edit-redemption-channel.policy";
import { CustomizedBack } from "store/navigation-wrapper";
import translate from "utils/translations";

export interface RedemptionChannelStore {
  isValidating: boolean;
  newRedemptionChannelData: RedemptionChannelRequestModel;
  redemptionChannelsData: PaginatedList<RedemptionChannelModel>;
  updateRedemptionChannelData: RedemptionChannelRequestModel;
  filter: Filter;
  searchingValue: string | undefined;
  tabFilterValue: FilterTab | undefined;
  branches: RedemptionChannelModel[];
}

const initialState: RedemptionChannelStore = {
  isValidating: false,
  updateRedemptionChannelData: {
    clientId: undefined,
    areaId: undefined,
    street: "",
    buildingNumber: "",
    contactNumber: "",
    latCoordinates: 0,
    lngCoordinates: 0,
  } as RedemptionChannelRequestModel,
  newRedemptionChannelData: {
    clientId: undefined,
    areaId: undefined,
    street: "",
    buildingNumber: "",
    contactNumber: "",
    latCoordinates: 0,
    lngCoordinates: 0,
  } as RedemptionChannelRequestModel,
  redemptionChannelsData: {
    list: [],
    pageSize: 10,
    pageIndex: 0,
    totalCount: 0,
  },
  filter: {
    orderBy: { key: "Street", sort: "asc" },
  },
  searchingValue: undefined,
  tabFilterValue: undefined,
  branches: [],
};

const slice = createSlice({
  name: "redemptionchannel",
  initialState,
  reducers: {
    setRedemptionChannelsFetchingData: (
      state: RedemptionChannelStore,
      action: PayloadAction<PaginatedList<RedemptionChannelModel>>,
    ) => {
      state.redemptionChannelsData = action.payload;
    },
    setBranchesFetchingData: (
      state: RedemptionChannelStore,
      action: PayloadAction<RedemptionChannelModel[]>,
    ) => {
      state.branches = action.payload;
    },
    setIsValidating: (
      state: RedemptionChannelStore,
      action: PayloadAction<boolean>,
    ) => {
      state.isValidating = action.payload;
    },
    setFilterTab: (
      state: RedemptionChannelStore,
      action: PayloadAction<FilterTab>,
    ) => {
      state.tabFilterValue = action.payload;
    },
    setRedemptionChannelEqualFilter: (
      state: RedemptionChannelStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setRedemptionChannelViewDynamicSearchFilter: (
      state: RedemptionChannelStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.filter = { ...state.filter, or: action.payload };
    },
    setSearchKey: (
      state: RedemptionChannelStore,
      action: PayloadAction<string>,
    ) => {
      state.searchingValue = action.payload;
    },
    setUpdateRedemptionChannelsData: (
      state: RedemptionChannelStore,
      action: PayloadAction<RedemptionChannelRequestModel>,
    ) => {
      state.updateRedemptionChannelData = action.payload;
    },
    setUpdateRedemptionChannelsDataProps: (
      state: RedemptionChannelStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        updateRedemptionChannelData: {
          ...state.updateRedemptionChannelData,
          [key]: value!,
        },
      };
    },
    setRedemptionChannelsPage: (
      state: RedemptionChannelStore,
      action: PayloadAction<number>,
    ) => {
      state.redemptionChannelsData.pageIndex = action.payload;
    },

    setNewRedemptionChannelData: (
      state: RedemptionChannelStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        newRedemptionChannelData: {
          ...state.newRedemptionChannelData,
          [key]: value!,
        },
      };
    },

    setTabFilterChange: (
      state: RedemptionChannelStore,
      action: PayloadAction<FilterTab>,
    ) => {
      state.tabFilterValue = action.payload;
    },

    addingRedemptionChannel: (state: RedemptionChannelStore) => {
      state.newRedemptionChannelData = initialState.newRedemptionChannelData;
      state.isValidating = false;
    },
    editingRedemptionChannel: (state: RedemptionChannelStore) => {
      state.updateRedemptionChannelData =
        initialState.updateRedemptionChannelData;
    },
    clearFilters: (state: RedemptionChannelStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
  },
});

export const {
  setRedemptionChannelsFetchingData,
  setRedemptionChannelEqualFilter,
  setRedemptionChannelViewDynamicSearchFilter,
  setSearchKey,
  setTabFilterChange,
  setRedemptionChannelsPage,
  setNewRedemptionChannelData,
  addingRedemptionChannel,
  editingRedemptionChannel,
  setIsValidating,
  setUpdateRedemptionChannelsData,
  setUpdateRedemptionChannelsDataProps,
  setBranchesFetchingData,
  clearFilters,
} = slice.actions;
export const reducer = slice.reducer;

export const getRedemptionChannelsData =
  (): AsyncAppThunk => async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var redemptionChannelPaging =
          store().redemptionchannel.redemptionChannelsData;
        var queryOptions = getFilterQuery(
          store().redemptionchannel.filter,
          redemptionChannelPaging.pageIndex * redemptionChannelPaging.pageSize,
          redemptionChannelPaging.pageSize,
        );

        const apiResponse = await getRedemptionChannelsService(queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setRedemptionChannelsFetchingData(apiResponse.result));
        }
      }),
    );
  };
export const getBranchesData = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      const apiResponse = await getRedemptionChannelsService();
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setBranchesFetchingData(apiResponse.result.list));
      }
    }),
  );
};
export const addRedemptionChannel =
  (): AsyncAppThunk => async (dispatch, store) => {
    var redemptionChannelState = store().redemptionchannel;
    dispatch(setIsValidating(true));
    if (
      Object.keys(ValidateAddRedemptionChannelState(store().redemptionchannel))
        .length === 0
    ) {
      dispatch(
        showLoaderWhile(async () => {
          const apiResponse = await addRedemptionChannelService(
            redemptionChannelState.newRedemptionChannelData,
          );
          if (
            apiResponse &&
            Object.keys(apiResponse).length > 0 &&
            apiResponse.result !== null
          ) {
            dispatch(setIsValidating(false));
            CustomizedBack();
            dispatch(addingRedemptionChannel());
            dispatch(
              addSnack({
                message: translate(
                  "Redemption_Channel_Added_Successfully",
                  "Redemption Channel Added Successfully",
                ),
              }),
            );
          }
        }),
      );
    }
  };
export const getRedemptionChannelDataById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getRedemptionChannelByIdService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setUpdateRedemptionChannelsData(apiResponse.result));
        }
      }),
    );
  };
export const editRedemptionChannel =
  (): AsyncAppThunk => async (dispatch, store) => {
    var redemptionChannelState = store().redemptionchannel;
    dispatch(setIsValidating(true));
    if (
      Object.keys(ValidateEditRedemptionChannelState(store().redemptionchannel))
        .length === 0
    ) {
      dispatch(
        showLoaderWhile(async () => {
          var updatedredemptionChannel = {
            ...redemptionChannelState.updateRedemptionChannelData,
            cityId: 1,
            countryId: 1,
          };
          const apiResponse = await editRedemptionChannelService(
            updatedredemptionChannel,
          );
          if (
            apiResponse &&
            Object.keys(apiResponse).length > 0 &&
            apiResponse.result !== null
          ) {
            dispatch(setIsValidating(false));
            CustomizedBack();
            dispatch(editingRedemptionChannel());
            dispatch(
              addSnack({
                message: translate(
                  "Redemption_Channel_Edited_Successfully",
                  "Redemption Channel Edited Successfully",
                ),
              }),
            );
          }
        }),
      );
    }
  };
